import { useEffect, useState } from "react";
import { api, ApiError } from "@/api/client";
import { READ_ONLY_TITLE, useReadOnly, useStore } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { IconCheck } from "@/components/SvoIcons";
import type { Topic, Vote } from "@offsite/shared";

export function VoteScreen() {
  const { state, dispatch } = useStore();
  // Solo-Offsites (AR) haben keine Voting-Phase. Der Bildschirm kann trotzdem
  // kurz montiert werden, bevor die Phase vom Server da ist — dann liefe
  // `/votes` in einen 403.
  const { soloTeams } = useOffsiteConfig();
  const readOnly = useReadOnly();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [myVote, setMyVote] = useState<Vote | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (soloTeams) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [t, v] = await Promise.all([
          api.topics.list(state.auth.token),
          api.votes.me(state.auth.token),
        ]);
        if (cancelled) return;
        setTopics(t.topics);
        dispatch({ type: "topics/set", topics: t.topics });
        setMyVote((prev) => {
          // If presenter resets all votes mid-session, our previously-stored
          // vote vanishes server-side. Detect that and flip back to the choice
          // UI without the participant having to refresh.
          if (prev && !v.vote) {
            setSelected(null);
            setError(null);
          }
          return v.vote;
        });
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [state.auth.token, dispatch, soloTeams]);

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.votes.cast(state.auth.token, selected);
      setMyVote(res.vote);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("Du hast schon abgestimmt.");
        try {
          const v = await api.votes.me(state.auth.token);
          setMyVote(v.vote);
        } catch {
          /* noop */
        }
      } else {
        setError("Abstimmung fehlgeschlagen — versuche es noch mal.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (myVote) {
    const votedTopic = topics.find((t) => t.id === myVote.topicId);
    return (
      <section
        style={{
          display: "grid",
          gap: 16,
          textAlign: "center",
          padding: "2rem 0",
        }}
      >
        <div className="check-badge">
          <IconCheck size={32} />
        </div>
        <div className="deck-eyebrow">Stimme abgegeben</div>
        <h2
          className="deck-title"
          style={{ fontSize: "clamp(1.4rem, 5vw, 2rem)", margin: 0 }}
        >
          Danke!
        </h2>
        <p className="deck-sub" style={{ margin: 0 }}>
          Du hast für{" "}
          <strong style={{ color: "var(--svo-dunkelblau)" }}>
            {votedTopic?.title ?? "dieses Thema"}
          </strong>{" "}
          abgestimmt. Warte kurz — gleich kommen die Ergebnisse auf den Beamer.
        </p>
      </section>
    );
  }

  return (
    <section style={{ display: "grid", gap: 16 }}>
      <header>
        <div className="deck-eyebrow">Abstimmung läuft</div>
        <h2
          className="deck-title"
          style={{ fontSize: "clamp(1.4rem, 5vw, 1.85rem)", margin: "4px 0 0" }}
        >
          Woran wollen wir heute arbeiten?
        </h2>
        <p className="deck-sub" style={{ marginTop: 8 }}>
          Eine Stimme pro Person — überleg dir's gut.
        </p>
      </header>

      <div style={{ display: "grid", gap: 12 }}>
        {topics.length === 0 && (
          <p className="form-hint">Themen werden geladen …</p>
        )}
        {topics.map((t, i) => {
          const isSelected = selected === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t.id)}
              className={`choice-row rise rise-${Math.min(i + 1, 6)}${
                isSelected ? " is-selected" : ""
              }`}
            >
              <div className="choice-num">{i + 1}</div>
              <span className="choice-title">{t.title}</span>
            </button>
          );
        })}
      </div>

      {error && <p className="form-error">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={!selected || busy || readOnly}
        className={`btn btn-primary btn-block btn-lg${readOnly ? " is-readonly" : ""}`}
        title={readOnly ? READ_ONLY_TITLE : undefined}
      >
        {busy ? "…" : "Abstimmen"}
      </button>
    </section>
  );
}
